<script setup lang="ts">
import { computed } from 'vue';
import { UiCard, UiText, UiIcon, UiChip, UiProgressBar, UiAccordion, UiButton, UiIconButton, UiTooltip, type ProjectReviewStatus } from '@shopexperts/shared';

const props = defineProps<{ status: ProjectReviewStatus; canShare?: boolean; isLoadingShare?: boolean; side?: string }>();
const emit = defineEmits<{ share: [reviewId: number]; edit: []; reply: [] }>();
const review = computed(() => props.status.review);
const presentation = computed(() => {
  switch (review.value?.status) {
    case 'published': return { label: 'Published', tone: 'success' as const, description: 'Visible on the expert’s profile.' };
    case 'pending': return { label: 'Awaiting moderation', tone: 'warn' as const, description: 'Submitted and waiting for the team to review.' };
    case 'draft': return { label: 'Draft', tone: 'neutral' as const, description: 'This review has not been published.' };
    case 'unpublished': return { label: 'Unpublished', tone: 'neutral' as const, description: 'Not visible on the expert’s profile.' };
    default: return { label: 'Submitted', tone: 'neutral' as const, description: 'A review has already been submitted for this project.' };
  }
});
const submittedDate = computed(() => {
  if (!review.value?.created_at) return '';
  const date = new Date(review.value.created_at);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
});
const ratings = computed(() => review.value ? [
  { label: 'Quality of work', score: review.value.quality },
  { label: 'Communication', score: review.value.communication },
  { label: 'Timelines', score: review.value.timelines },
  { label: 'Value for money', score: review.value.value_for_money },
] : []);
</script>

<template>
  <UiCard>
    <template #preview>
      <div class="bg-se-success-light/60 border-b border-se-gray-200 p-4 flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <UiText as="h3" variant="h6" text="Project review" />
          <div class="flex items-center gap-2">
            <UiChip :text="presentation.label" :variant="presentation.tone" size="xs" rounded="full" />
            <UiTooltip v-if="canShare && review?.status === 'published'" content="Share review">
              <UiIconButton icon="share-07" aria-label="Share review" :disabled="isLoadingShare" @click="emit('share', review.id)" />
            </UiTooltip>
          </div>
        </div>
        <div v-if="review" class="flex items-center gap-3">
          <UiText as="p" variant="h2" class="tabular-nums" :text="review.overall_rating.toFixed(1)" />
          <div class="flex flex-col gap-1">
            <div class="flex gap-0.5" aria-hidden="true">
              <div v-for="star in 5" :key="star" class="relative size-4">
                <UiIcon name="star" class="size-4 grayscale opacity-25" />
                <div class="absolute inset-y-0 left-0 overflow-hidden" :style="{ width: `${Math.min(1, Math.max(0, review.overall_rating - star + 1)) * 100}%` }">
                  <UiIcon name="star" class="size-4 text-se-green" />
                </div>
              </div>
            </div>
            <UiText as="p" variant="caption-normal" class="text-se-gray-700" text="Overall rating out of 5" />
          </div>
        </div>
      </div>
    </template>
    <div class="flex flex-col gap-4">
      <template v-if="review">
        <UiChip v-if="review.is_anonymous" text="Posted anonymously" icon="user-circle" variant="neutral" size="xs" class="self-start" />
        <UiText as="p" variant="p-small" class="whitespace-pre-wrap break-words border-l-2 border-se-primary pl-3" :text="review.review_text" />
        <UiAccordion>
          <template #trigger="{ open }">
            <UiButton label="Rating details" variant="ghost" justify="start" class="flex-1 !px-0 !py-0 !min-h-0" :aria-expanded="open" />
          </template>
          <div class="flex flex-col gap-3 pt-3">
          <div v-for="rating in ratings" :key="rating.label" class="flex flex-col gap-1">
            <div class="flex justify-between gap-2">
              <UiText as="span" variant="caption-normal" class="text-se-gray-700" :text="rating.label" />
              <UiText as="span" variant="caption-normal" :text="`${rating.score} / 5`" />
            </div>
            <UiProgressBar :value="rating.score" :max="5" size="sm" tone="green" :aria-label="`${rating.label}: ${rating.score} out of 5`" />
          </div>
          </div>
        </UiAccordion>
      </template>
      <div v-if="review?.review_response" class="rounded-lg bg-se-gray-100 p-3 flex flex-col gap-2">
        <UiText as="h4" variant="h6" :text="side === 'expert' ? 'Your reply' : 'Expert’s reply'" />
        <UiText as="p" variant="p-small" class="whitespace-pre-wrap break-words" :text="review.review_response.response_text" />
      </div>
      <UiButton v-if="side === 'client' && status.can_edit_review" label="Edit review" icon="pencil" variant="outline" size="sm" @click="emit('edit')" />
      <template v-if="side === 'expert' && review">
        <UiButton v-if="status.can_reply" :label="review.review_response ? 'Edit your reply' : 'Write a reply'" icon="pencil" variant="outline" size="sm" class="w-full" @click="emit('reply')" />
        <UiTooltip v-else-if="status.reply_requires_pro" content="Pro experts can reply to client reviews.">
          <div class="w-full">
            <UiButton :label="review.review_response ? 'Edit your reply' : 'Write a reply'" icon="pencil" variant="outline" size="sm" class="w-full" disabled>
              <template #after_label>
                <UiChip text="PRO" size="xs" variant="dark" class="ml-1" />
              </template>
            </UiButton>
          </div>
        </UiTooltip>
      </template>
      <div class="flex flex-col gap-1 border-t border-se-gray-200 pt-3">
        <UiText v-if="submittedDate" as="p" variant="caption-normal" class="text-se-gray-700" :text="`Submitted ${submittedDate}`" />
        <UiText as="p" variant="caption-normal" class="text-se-gray-700" :text="presentation.description" />
      </div>
    </div>
  </UiCard>
</template>
